import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Search, ArrowRight, ShieldCheck, ChevronDown, Info } from "lucide-react";
import LocaleLink from "@/components/LocaleLink";
import { usePlatformMetrics } from '@/hooks/usePlatformMetrics';
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";

import { Reveal } from "@/components/ui/reveal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { FRAMEWORK_DESCRIPTIONS, JURISDICTION_LABELS } from "@/data/frameworkDescriptions";
import FrameworkLogo from "@/components/marketing/FrameworkLogo";
import { cn } from "@/lib/utils";

export type Category = "security" | "privacy" | "industry" | "financial" | "ai" | "regional";

type Framework = { name: string; category: Category; popular?: boolean; jur?: string };

export const FRAMEWORKS: Framework[] = [
  // ---------- Security ----------
  { name: "SOC 2", category: "security", popular: true, jur: "US" },
  { name: "ISO 27001", category: "security", popular: true, jur: "INTL" },
  { name: "ISO 27017", category: "security", jur: "INTL" },
  { name: "ISO 27018", category: "security", jur: "INTL" },
  { name: "NIST CSF", category: "security", popular: true, jur: "US" },
  { name: "NIST 800-53", category: "security", jur: "US" },
  { name: "NIST 800-171", category: "security", jur: "US" },
  { name: "CIS Controls v8", category: "security", jur: "INTL" },
  { name: "FedRAMP", category: "security", jur: "US" },
  { name: "CMMC 2.0", category: "security", jur: "US" },
  { name: "TISAX", category: "security", jur: "DE" },
  { name: "BSI C5", category: "security", jur: "DE" },
  { name: "ENS", category: "security", jur: "ES" },
  { name: "Cyber Essentials", category: "security", jur: "UK" },
  { name: "IRAP", category: "security", jur: "AU" },
  { name: "SOC 1", category: "security", jur: "US" },
  { name: "SOC 3", category: "security", jur: "US" },
  { name: "ISO 22301", category: "security", jur: "INTL" },
  { name: "EU NIS2", category: "security", popular: true, jur: "EU" },
  { name: "EU Cyber Resilience Act", category: "security", jur: "EU" },

  // ---------- Privacy ----------
  { name: "GDPR", category: "privacy", popular: true, jur: "EU" },
  { name: "UK GDPR", category: "privacy", jur: "UK" },
  { name: "CCPA / CPRA", category: "privacy", popular: true, jur: "US" },
  { name: "ISO 27701", category: "privacy", jur: "INTL" },
  { name: "LGPD", category: "privacy", jur: "BR" },
  { name: "PIPEDA", category: "privacy", jur: "CA" },
  { name: "POPIA", category: "privacy", jur: "ZA" },
  { name: "PDPA Singapore", category: "privacy", jur: "SG" },
  { name: "APPI", category: "privacy", jur: "JP" },
  { name: "VCDPA", category: "privacy", jur: "US" },
  { name: "CPA Colorado", category: "privacy", jur: "US" },
  { name: "Saudi PDPL", category: "privacy", jur: "SA" },
  { name: "UAE PDPL", category: "privacy", jur: "AE" },
  { name: "K-ISMS", category: "privacy", jur: "KR" },
  { name: "ePrivacy Directive", category: "privacy", jur: "EU" },
  { name: "Data Protection Act 2018", category: "privacy", jur: "UK" },
  { name: "Australian Privacy Act", category: "privacy", jur: "AU" },
  { name: "EU Data Act", category: "privacy", jur: "EU" },

  // ---------- Industry ----------
  { name: "HIPAA", category: "industry", popular: true, jur: "US" },
  { name: "HITRUST CSF", category: "industry", jur: "US" },
  { name: "PCI DSS 4.0", category: "industry", popular: true, jur: "INTL" },
  { name: "FERPA", category: "industry", jur: "US" },
  { name: "GLBA", category: "industry", jur: "US" },
  { name: "FFIEC", category: "industry", jur: "US" },
  { name: "NERC CIP", category: "industry", jur: "US" },
  { name: "TX-RAMP", category: "industry", jur: "US" },
  { name: "StateRAMP", category: "industry", jur: "US" },
  { name: "GxP / 21 CFR Part 11", category: "industry", jur: "US" },
  { name: "ISO 13485", category: "industry", jur: "INTL" },

  // ---------- Financial ----------
  { name: "SOX", category: "financial", popular: true, jur: "US" },
  { name: "EU DORA", category: "financial", popular: true, jur: "EU" },
  { name: "PSD2", category: "financial", jur: "EU" },
  { name: "PSD3", category: "financial", jur: "EU" },
  { name: "MiCA", category: "financial", jur: "EU" },
  { name: "MiFID II", category: "financial", jur: "EU" },
  { name: "MiFIR", category: "financial", jur: "EU" },
  { name: "EMIR", category: "financial", jur: "EU" },
  { name: "EMIR Refit", category: "financial", jur: "EU" },
  { name: "UK EMIR", category: "financial", jur: "UK" },
  { name: "MAR", category: "financial", jur: "EU" },
  { name: "SFTR", category: "financial", jur: "EU" },
  { name: "CSDR", category: "financial", jur: "EU" },
  { name: "UCITS", category: "financial", jur: "EU" },
  { name: "AIFMD", category: "financial", jur: "EU" },
  { name: "PRIIPs", category: "financial", jur: "EU" },
  { name: "AMLD VI", category: "financial", jur: "EU" },
  { name: "EU Travel Rule (TFR)", category: "financial", jur: "EU" },
  { name: "Basel III / IV", category: "financial", jur: "INTL" },
  { name: "FCA Consumer Duty", category: "financial", jur: "UK" },
  { name: "FCA Operational Resilience", category: "financial", jur: "UK" },
  { name: "FCA Travel Rule", category: "financial", jur: "UK" },
  { name: "FINRA Rule 3110", category: "financial", jur: "US" },
  { name: "FINRA Rule 2210", category: "financial", jur: "US" },
  { name: "FINRA Rule 2111", category: "financial", jur: "US" },
  { name: "SEC Marketing Rule", category: "financial", jur: "US" },
  { name: "SEC Cybersecurity Rules", category: "financial", jur: "US" },
  { name: "SEC Climate Rule", category: "financial", jur: "US" },
  { name: "Form CRS", category: "financial", jur: "US" },
  { name: "Bank Secrecy Act (BSA)", category: "financial", jur: "US" },
  { name: "FinCEN Travel Rule", category: "financial", jur: "US" },
  { name: "FATCA", category: "financial", jur: "US" },
  { name: "CRS", category: "financial", jur: "INTL" },
  { name: "AML / CFT", category: "financial", popular: true, jur: "INTL" },
  { name: "KYC", category: "financial", jur: "INTL" },
  { name: "MAS TRM", category: "financial", jur: "SG" },
  { name: "MAS Notice PSN02", category: "financial", jur: "SG" },
  { name: "HKMA Operational Resilience", category: "financial", jur: "HK" },
  { name: "APRA CPS 230", category: "financial", jur: "AU" },
  { name: "APRA CPS 234", category: "financial", jur: "AU" },
  { name: "AU AML/CTF Act", category: "financial", jur: "AU" },
  { name: "FINTRAC", category: "financial", jur: "CA" },
  { name: "OSFI B-13", category: "financial", jur: "CA" },
  { name: "NZ AML/CFT Act", category: "financial", jur: "NZ" },

  // ---------- AI / Emerging ----------
  { name: "ISO 42001", category: "ai", popular: true, jur: "INTL" },
  { name: "EU AI Act", category: "ai", popular: true, jur: "EU" },
  { name: "NIST AI RMF", category: "ai", jur: "US" },
  { name: "ISO 23894", category: "ai", jur: "INTL" },
  { name: "Singapore AI Verify", category: "ai", jur: "SG" },
  { name: "UK AI Safety Framework", category: "ai", jur: "UK" },

  // ---------- Regional / Sector ----------
  { name: "Australia Digital ID Act", category: "regional", jur: "AU" },
  { name: "ASIC Whistleblower", category: "regional", jur: "AU" },
  { name: "Ireland DPA 2018", category: "regional", jur: "IE" },
  { name: "Switzerland FADP", category: "regional", jur: "CH" },
  { name: "Türkiye KVKK", category: "regional", jur: "TR" },
  { name: "India DPDP Act", category: "regional", jur: "IN" },
  { name: "Thailand PDPA", category: "regional", jur: "TH" },
  { name: "Indonesia PDP Law", category: "regional", jur: "ID" },
  { name: "Kenya DPA", category: "regional", jur: "KE" },
  { name: "Nigeria NDPA", category: "regional", jur: "NG" },
  { name: "Mexico LFPDPPP", category: "regional", jur: "MX" },
  { name: "Argentina PDPL", category: "regional", jur: "AR" },
];


const FILTERS: { id: "all" | Category; label: string }[] = [
  { id: "all", label: "All" },
  { id: "security", label: "Security" },
  { id: "privacy", label: "Privacy" },
  { id: "financial", label: "Financial" },
  { id: "industry", label: "Industry" },
  { id: "ai", label: "AI" },
  { id: "regional", label: "Regional" },
];

export const CATEGORY_LABELS: Record<Category, string> = {
  security: "Security",
  privacy: "Privacy",
  industry: "Industry",
  financial: "Financial",
  ai: "AI",
  regional: "Regional",
};

const FrameworkCoverageFull = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState<"all" | Category>("all");


  const searchMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FRAMEWORKS;
    return FRAMEWORKS.filter(
      (f) => f.name.toLowerCase().includes(q) || (f.jur && f.jur.toLowerCase().includes(q))
    );
  }, [query]);

  const filtered = useMemo(
    () =>
      activeCategory === "all"
        ? searchMatches
        : searchMatches.filter((f) => f.category === activeCategory),
    [searchMatches, activeCategory]
  );

  const grouped = useMemo(() => {
    const order: Category[] = ["security", "privacy", "financial", "industry", "ai", "regional"];
    return order
      .map((c) => [c, filtered.filter((f) => f.category === c)] as const)
      .filter(([, items]) => items.length > 0);
  }, [filtered]);

  // Mobile accordion state. First group open by default; searching auto-expands
  // every group that has matches.
  const [manualGroups, setManualGroups] = useState<Set<Category> | null>(null);
  const [openDetail, setOpenDetail] = useState<string | null>(null);

  const openGroups = useMemo<Set<Category>>(() => {
    if (manualGroups) return manualGroups;
    if (query.trim()) return new Set(grouped.map(([c]) => c));
    const hash =
      typeof window !== "undefined"
        ? (window.location.hash.replace("#", "") as Category)
        : ("" as Category);
    const hashed = grouped.find(([c]) => c === hash);
    if (hashed) return new Set([hashed[0]]);
    return new Set(grouped.length ? [grouped[0][0]] : []);
  }, [manualGroups, query, grouped]);


  const toggleGroup = (category: Category) => {
    const next = new Set(openGroups);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    setManualGroups(next);
  };

  // Reset manual expansion whenever the result set changes.
  useEffect(() => {
    setManualGroups(null);
    setOpenDetail(null);
  }, [query, activeCategory]);



  const totalCount = FRAMEWORKS.length;
  const jurisdictionCount = useMemo(
    () => new Set(FRAMEWORKS.map((f) => f.jur).filter(Boolean)).size,
    []
  );
  const categoryCount = useMemo(
    () => new Set(FRAMEWORKS.map((f) => f.category)).size,
    []
  );

  return (
    <section className="py-12 bg-muted/30" id="coverage">
      <div className="container mx-auto px-6">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-8">
            <p className="eyebrow mb-3">
              {t("frameworkMatrix.eyebrow")}
            </p>
            <h2 className="section-title-fluid font-bold mb-4">
              {t("frameworkMatrix.title")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("frameworkMatrix.subtitle")}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.03}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mb-8">
            <div className="border border-border bg-card px-4 py-3 text-center">
              <div className="text-2xl font-bold text-primary tabular-nums">{totalCount}</div>
              <div className="text-xs text-muted-foreground">{t("frameworkMatrix.statFrameworks")}</div>
            </div>
            <div className="border border-border bg-card px-4 py-3 text-center">
              <div className="text-2xl font-bold text-primary tabular-nums">{jurisdictionCount}</div>
              <div className="text-xs text-muted-foreground">{t("frameworkMatrix.statJurisdictions")}</div>
            </div>
            <div className="border border-border bg-card px-4 py-3 text-center">
              <div className="text-2xl font-bold text-primary tabular-nums">{categoryCount}</div>
              <div className="text-xs text-muted-foreground">{t("frameworkMatrix.statCategories")}</div>
            </div>
            <div className="border border-border bg-card px-4 py-3 text-center">
              <div className="text-2xl font-bold text-primary">+</div>
              <div className="text-xs text-muted-foreground">{t("frameworkMatrix.statMonthly")}</div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="max-w-4xl mx-auto">
            {/* Search */}
            <div className="relative mb-5">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("frameworkMatrix.searchPlaceholder")}
                className="pl-12 pr-4 h-12 text-base bg-card border-border"
                aria-label="Search frameworks"
              />
            </div>

            {/* Category filters */}
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {FILTERS.map((f) => {
                const count =
                  f.id === "all"
                    ? searchMatches.length
                    : searchMatches.filter((fw) => fw.category === f.id).length;
                const isActive = activeCategory === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setActiveCategory(f.id)}
                    aria-pressed={isActive}
                    className={cn(
                      "inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
                    )}
                  >
                    {f.label}
                    <span
                      className={cn(
                        "font-mono tabular-nums text-[10px]",
                        isActive ? "opacity-80" : "opacity-60"
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Results counter */}
            {grouped.length > 0 && (
              <p className="mb-4 text-center text-xs text-muted-foreground">
                <strong className="text-foreground tabular-nums">{filtered.length}</strong>{" "}
                {t("frameworkMatrix.resultsAcross")}{" "}
                <strong className="text-foreground tabular-nums">{grouped.length}</strong>{" "}
                {t("frameworkMatrix.categoriesWord")}
              </p>
            )}

            {/* Cluster board — collapsible groups on mobile, open panels on desktop */}
            {grouped.length > 0 ? (
              <TooltipProvider delayDuration={150}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {grouped.map(([category, items]) => {
                    const isOpen = openGroups.has(category);
                    return (
                      <div key={category} id={category} className="scroll-mt-28 border border-border bg-card">
                        <button
                          type="button"
                          onClick={() => toggleGroup(category)}
                          aria-expanded={isOpen}
                          className="flex w-full items-center justify-between gap-2 border-b border-border px-4 py-3 text-left md:cursor-default md:pointer-events-none"
                        >
                          <span className="flex items-baseline gap-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                              {t(`frameworkMatrix.categories.${category}`, CATEGORY_LABELS[category])}
                            </h3>
                            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                              {items.length}
                            </span>
                          </span>
                          <ChevronDown
                            aria-hidden
                            className={cn(
                              "h-4 w-4 shrink-0 text-muted-foreground transition-transform md:hidden",
                              isOpen && "rotate-180"
                            )}
                          />
                        </button>

                        <div className={cn("p-4", !isOpen && "hidden md:block")}>
                          <div className="flex flex-wrap gap-2">
                            {items.map((f) => {
                              const description = FRAMEWORK_DESCRIPTIONS[f.name];
                              const jurLabel = f.jur ? JURISDICTION_LABELS[f.jur] ?? f.jur : null;
                              const detailOpen = openDetail === f.name;
                              return (
                                <div key={f.name} className="contents">
                                 <div className="inline-flex items-stretch md:contents">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <LocaleLink
                                        to="/templates"
                                        className={cn(
                                          "group inline-flex min-h-[40px] items-center gap-2 border px-3 py-1.5 text-sm transition-colors md:min-h-0",
                                          f.popular
                                            ? "border-primary/40 bg-primary/5 font-semibold text-foreground hover:border-primary hover:bg-primary/10"
                                            : "border-border bg-background font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground"
                                        )}
                                      >
                                        {f.popular && (
                                          <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
                                        )}
                                        <FrameworkLogo name={f.name} size={16} />
                                        <span>{f.name}</span>
                                        {f.jur && (
                                          <span className="border-l border-border pl-2 font-mono text-[10px] uppercase text-muted-foreground">
                                            {f.jur}
                                          </span>
                                        )}
                                      </LocaleLink>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="hidden max-w-xs overflow-hidden p-0 md:block">
                                      <div className="space-y-2 p-3">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="text-sm font-semibold leading-tight">{f.name}</div>
                                          <span className="shrink-0 border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                            {t(`frameworkMatrix.categories.${f.category}`, CATEGORY_LABELS[f.category])}
                                          </span>
                                        </div>
                                        {jurLabel && (
                                          <div className="text-xs text-muted-foreground">{jurLabel}</div>
                                        )}
                                        {description && (
                                          <p className="border-t border-border pt-2 text-xs leading-relaxed text-muted-foreground">
                                            {description}
                                          </p>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>

                                  {/* Touch: inline detail toggle */}
                                  {(description || jurLabel) && (
                                    <button
                                      type="button"
                                      onClick={() => setOpenDetail(detailOpen ? null : f.name)}
                                      aria-expanded={detailOpen}
                                      aria-label={`About ${f.name}`}
                                      className="inline-flex h-10 w-8 items-center justify-center border border-border bg-background text-muted-foreground md:hidden"
                                    >
                                      <Info className="h-3.5 w-3.5" aria-hidden />
                                    </button>
                                  )}
                                 </div>

                                  {detailOpen && (
                                    <div className="w-full border border-border bg-muted/40 p-3 md:hidden">
                                      {jurLabel && (
                                        <div className="text-xs font-medium text-foreground">{jurLabel}</div>
                                      )}
                                      {description && (
                                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                                          {description}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TooltipProvider>
            ) : (
              <div className="flex flex-col items-center justify-center border border-border bg-card py-12 text-center">
                <p className="mb-3 text-muted-foreground">
                  {t("frameworkMatrix.noResults")}{" "}
                  <strong>"{query}"</strong>
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("frameworkMatrix.noResultsHint")}
                </p>
              </div>
            )}

            {/* Footer summary + CTA */}
            <div className="justify-between mt-6 flex flex-col gap-4 sm:grid sm:w-fit sm:grid-flow-col sm:auto-cols-fr">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Badge variant="secondary" className="font-mono tabular-nums">
                  {filtered.length}
                </Badge>
                <span>
                  {t("frameworkMatrix.showingOf")}{" "}
                  <strong className="text-foreground">{totalCount}+</strong>{" "}
                  {t("frameworkMatrix.frameworksWord")}{" "}
                  <strong className="text-foreground">{jurisdictionCount}</strong>{" "}
                  {t("frameworkMatrix.jurisdictionsWord")}
                </span>
              </div>
              <div className="flex gap-2">
                <Button className="w-full" asChild variant="outline" size="sm">
                  <LocaleLink to="/contact">
                    {t("frameworkMatrix.requestFramework")}
                  </LocaleLink>
                </Button>
                <Button className="w-full" asChild size="sm">
                  <LocaleLink
                    to="/framework-library"
                    className="inline-flex items-center gap-1.5"
                  >
                    {t("frameworkMatrix.browseAll")}
                    <ArrowRight className="w-4 h-4" />
                  </LocaleLink>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="text-center text-sm text-muted-foreground mt-8">
            {t("frameworkMatrix.footnote")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------------------------------------------------------------------
 * Summary variant — homepage. Shows category tiles plus a typeahead lookup that
 * hands off to /framework-library. The full board lives on that page.
 * ------------------------------------------------------------------------- */

export const CATEGORY_ORDER: Category[] = [
  "security",
  "privacy",
  "financial",
  "industry",
  "ai",
  "regional",
];

/**
 * `inline` drops this block's own section chrome — background, padding,
 * eyebrow, title, subtitle — and compresses the category tiles into a single
 * rail, so it can sit inside another section.
 *
 * It exists because the homepage carried two six-tile grids that each named
 * three regulations per tile, a few hundred pixels apart: the content packs and
 * these category tiles. The same regulations appeared in both (SOC 2 and ISO
 * 27001 under Security and again under Americas; GDPR and CCPA under Privacy
 * and again under Privacy Global). One grid was enough. The packs kept theirs,
 * because a pack is a thing you adopt and carries a control count; coverage is
 * breadth, and breadth reads fine as numbers on a rail.
 *
 * The typeahead comes along, because "look up the standard you need" is the
 * best entry point in the section and costs one row.
 */
const FrameworkCoverageSummary = ({ inline = false }: { inline?: boolean }) => {
  const { t } = useTranslation();
  const m = usePlatformMetrics();
  const navigate = useLocaleNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const totalCount = FRAMEWORKS.length;
  const jurisdictionCount = useMemo(
    () => new Set(FRAMEWORKS.map((f) => f.jur).filter(Boolean)).size,
    []
  );

  const categories = useMemo(
    () =>
      CATEGORY_ORDER.map((c) => {
        const items = FRAMEWORKS.filter((f) => f.category === c);
        const popular = items.filter((f) => f.popular);
        const samples = Array.from(new Set([...popular, ...items])).slice(0, 3);
        return { category: c, count: items.length, samples };
      }).filter((g) => g.count > 0),
    []
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return FRAMEWORKS.filter(
      (f) => f.name.toLowerCase().includes(q) || (f.jur && f.jur.toLowerCase().includes(q))
    );
  }, [query]);

  const visible = matches.slice(0, 8);

  useEffect(() => setHighlight(0), [query]);

  // Close the listbox on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const goTo = (f: Framework) => {
    setOpen(false);
    navigate(`/framework-library?q=${encodeURIComponent(f.name)}#${f.category}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!visible.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h + 1) % visible.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (h - 1 + visible.length) % visible.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      goTo(visible[highlight] ?? visible[0]);
    }
  };

  const showPanel = open && query.trim().length >= 2;

  const Chrome = inline ? Fragment : SummarySection;

  return (
    <Chrome>
        {!inline && (
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-8">
            <p className="eyebrow mb-3">
              {t("frameworkMatrix.eyebrow")}
            </p>
            <h2 className="section-title-fluid font-bold mb-4">
              {t("frameworkMatrix.summaryTitle")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("frameworkMatrix.summarySubtitle")}
            </p>
          </div>
        </Reveal>
        )}

        <Reveal delay={0.05}>
          <div ref={wrapRef} className={inline ? "relative max-w-2xl mx-auto mb-5 laptop:mb-3" : "relative max-w-2xl mx-auto mb-8"}>
            <Search
              aria-hidden
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground"
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={showPanel}
              aria-controls="framework-lookup-results"
              aria-autocomplete="list"
              placeholder={t("frameworkMatrix.lookupPlaceholder")}
              className="pl-12 pr-4 h-12 laptop:h-10 text-base bg-card border-border"
              aria-label={t("frameworkMatrix.lookupLabel")}
            />

            {showPanel && (
              <div className="absolute z-20 mt-1 w-full border border-border bg-card shadow-sm">
                {visible.length > 0 ? (
                  <ul id="framework-lookup-results" role="listbox" className="max-h-80 overflow-y-auto">
                    {visible.map((f, i) => (
                      <li key={f.name} role="option" aria-selected={i === highlight}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlight(i)}
                          onClick={() => goTo(f)}
                          className={cn(
                            "flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm",
                            i === highlight ? "bg-muted text-foreground" : "text-muted-foreground"
                          )}
                        >
                          <span className="flex items-center gap-2 font-medium text-foreground">
                            {f.popular && (
                              <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
                            )}
                            <FrameworkLogo name={f.name} size={16} />
                            {f.name}
                          </span>
                          <span className="font-mono text-[10px] uppercase text-muted-foreground">
                            {t(`frameworkMatrix.categories.${f.category}`, CATEGORY_LABELS[f.category])}
                            {f.jur ? ` · ${f.jur}` : ""}
                          </span>
                        </button>
                      </li>
                    ))}
                    {matches.length > visible.length && (
                      <li className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
                        {matches.length - visible.length}{" "}
                        {t("frameworkMatrix.moreResults")}
                      </li>
                    )}
                  </ul>
                ) : (
                  <div id="framework-lookup-results" className="px-4 py-4 text-sm text-muted-foreground">
                    {t("frameworkMatrix.lookupEmpty")}{" "}
                    <LocaleLink to="/contact" className="text-primary underline underline-offset-2">
                      {t("frameworkMatrix.requestFramework")}
                    </LocaleLink>
                  </div>
                )}
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          {inline ? (
            // Breadth as a rail: the count is the claim, and the example names
            // are already on the pack tiles above.
            // Families and counts come from the live metrics feed, derived
            // from frameworks_registry, and are only accepted if they still sum
            // to the picker count printed below them. The
            // component's own FRAMEWORKS array is a superset used for the
            // typeahead; counting it here is what produced the unsupported 111.
            <div>
              <ul className="flex flex-wrap items-stretch justify-center gap-px border border-border bg-border">
                {m.framework_families.map((fam) => (
                  <li key={fam.key} className="min-w-[7.5rem] flex-1">
                    <LocaleLink
                      to="/framework-library"
                      className="flex h-full flex-col items-center gap-0.5 bg-card px-3 py-3 laptop:py-2 text-center transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      <span className="font-mono text-lg font-semibold leading-none tabular-nums text-foreground">
                        {fam.count}
                      </span>
                      <span className="text-xs leading-snug text-muted-foreground">
                        {t(`frameworkMatrix.families.${fam.key}`, fam.label ?? fam.key)}
                      </span>
                    </LocaleLink>
                  </li>
                ))}
              </ul>
              <p className="mt-2 laptop:mt-1 text-center text-xs text-muted-foreground">
                {t('frameworkMatrix.familyTotal', {
                  n: m.frameworks_in_picker,
                })}
              </p>
            </div>
          ) : (
          <ul className="grid grid-cols-2 gap-3 md:grid-cols-3 max-w-4xl mx-auto">
            {categories.map(({ category, count, samples }) => (
              <li key={category}>
                <LocaleLink
                  to={`/framework-library#${category}`}
                  className="flex h-full flex-col border border-border bg-card p-4 transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">
                      {t(`frameworkMatrix.categories.${category}`, CATEGORY_LABELS[category])}
                    </h3>
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                      {count}
                    </span>
                  </span>
                  <span className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {samples.map((s) => s.name).join(" · ")}
                  </span>
                </LocaleLink>
              </li>
            ))}
          </ul>
          )}
        </Reveal>

        <Reveal delay={0.12}>
          <div className={inline ? "mt-4 laptop:mt-2 flex justify-center" : "mt-8 flex justify-center"}>
            <Button asChild size="sm" variant={inline ? "outline" : "default"}>
              <LocaleLink to="/framework-library" className="inline-flex items-center gap-1.5">
                {t("frameworkMatrix.browseAll")}
                <ArrowRight className="w-4 h-4" aria-hidden />
              </LocaleLink>
            </Button>
          </div>
        </Reveal>
    </Chrome>
  );
};

/** The standalone chrome, used when this block is its own section. */
const SummarySection = ({ children }: { children: React.ReactNode }) => (
  <section className="py-12 bg-muted/30" id="coverage">
    <div className="container mx-auto px-6">{children}</div>
  </section>
);

/** Search + category rail + browse link, for embedding in another section. */
export const FrameworkCoverageInline = () => <FrameworkCoverageSummary inline />;

type FrameworkCoverageMatrixProps = { variant?: "full" | "summary" };

const FrameworkCoverageMatrix = ({ variant = "full" }: FrameworkCoverageMatrixProps) =>
  variant === "summary" ? <FrameworkCoverageSummary /> : <FrameworkCoverageFull />;

export default FrameworkCoverageMatrix;

